import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import {
  addResidenceHistory,
  removeResidenceHistory,
  persistResidenceHistory,
  navigateWizard,
  addPageData,
} from '../../actions';
import { fetchStatesRequest } from '../../actions/fetchStatesActions';
import AddActionBar from '../../components/AddActionBar/AddActionBar';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import ResidenceHistoryItem from '../../components/ResidenceHistoryItem';
import ResidenceSelection from '../../components/ResidenceSelection/ResidenceSelection';
import analytics from '../../lib/analytics';
import { fullDateFormat } from '../../lib/dateFormatter';
import useCountries from '../../lib/hooks/useCountries';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { subdivisionsSelector } from '../../reducers/selectors';

const PreviousResidencePage = ({
  states,
  builderUi,
  dispatchFetchStates,
  dispatchAddResidenceHistory,
  dispatchRemoveResidenceHistory,
  dispatchPersistResidenceHistory,
  dispatchWizardNav,
  dispatchAddPageData,
}) => {
  const { residenceCountry, residenceState, residenceHistory } = builderUi;
  const { data: countries } = useCountries();
  const [editState, setEditState] = useState(false);
  const [showMinDateMessage, setShowMinDateMessage] = useState(false);
  const threeYearsAgo = useMemo(() => {
    return new Date(new Date().setFullYear(new Date().getFullYear() - 3));
  }, []);
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    if (!residenceHistory || !residenceHistory.length) {
      setEditState(true);
    }
  }, [residenceHistory]);

  const deleteResidence = payload => {
    dispatchRemoveResidenceHistory(payload);
  };

  const handleResidenceUpdate = payload => {
    dispatchAddResidenceHistory(payload);
  };

  const fetchStatesForCountry = countryCode => {
    dispatchFetchStates(countryCode);
  };

  const stateName = (countryCode, stateCode) => {
    const countryStates = states[countryCode];
    if (!countryStates) {
      return `${countryCode}, ${stateCode}`;
    }
    const stateLabel = countryStates.find(countryState => countryState.value === stateCode);
    const countryLabel = countries.find(country => country.value === countryCode);
    return `${countryLabel?.label ?? countryCode}, ${stateLabel.label}`;
  };

  const currentResidence = {
    countryCode: residenceCountry?.value,
    stateCode: residenceState?.value,
  };

  const isCurrentResidence = (residenceHistoryItem, current) => {
    return JSON.stringify(residenceHistoryItem) === JSON.stringify(current);
  };

  const renderResidenceHistories = residenceHistory.map((residenceHistoryItem, index) => {
    const { countryCode, stateCode } = residenceHistoryItem;
    return (
      <CSSTransition key={`${countryCode}-${stateCode}`} timeout={300} classNames="fade-list-item">
        <ResidenceHistoryItem
          residenceHistoryItem={residenceHistoryItem}
          stateName={stateName(countryCode, stateCode)}
          deleteResidence={deleteResidence}
          isCurrentResidence={isCurrentResidence(residenceHistoryItem, currentResidence)}
          index={index}
        />
      </CSSTransition>
    );
  });

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'previous-residence');
    if (residenceHistory.length > 0) {
      dispatchPersistResidenceHistory();

      dispatchAddPageData(payloadFactory.createAddPageDataPayload('privacyPolicy', true));

      const { countryCode, stateCode, startMonth, startYear } = residenceHistory[0];
      const country = countries.find(country => country.value === countryCode);
      const countryStates = states[countryCode];
      const countryState = countryStates
        ? countryStates.find(state => state.value === stateCode)
        : [];

      const payload = payloadFactory.createAddPageDataPayload(
        'currentResidenceStartDate',
        { month: `${startMonth}`, year: `${startYear}` },
        'currentResidenceStartDate',
        fullDateFormat(new Date(startYear, startMonth, 1)),
      );
      dispatchAddPageData(payload);

      dispatchAddPageData(
        payloadFactory.createAddPageDataPayload(
          'residenceCountry',
          country,
          'residenceCountry',
          country.value,
        ),
      );

      dispatchAddPageData(
        payloadFactory.createAddPageDataPayload(
          'residenceState',
          countryState,
          'residenceState',
          countryState.value,
        ),
      );

      dispatchPersistResidenceHistory();
      dispatchWizardNav({ dir: 'fwd' });
    }
  };

  useEffect(() => {
    if (!residenceHistory.length || !countries.length) {
      setShowMinDateMessage(false);
      setIsInvalid(true);
    } else {
      let months = 0;
      const currentDate = new Date();
      let previousYear = currentDate.getFullYear();
      let previousMonth = currentDate.getMonth() + 1;

      residenceHistory.forEach(residence => {
        months += stayInMonths(
          residence.startYear,
          residence.startMonth + 1,
          previousYear,
          previousMonth,
        );
        previousYear = residence.startYear;
        previousMonth = residence.startMonth + 1;
      });

      if (months >= 36) {
        setShowMinDateMessage(false);
        setIsInvalid(false);
      } else {
        setShowMinDateMessage(true);
        setIsInvalid(true);
      }
    }
  }, [residenceHistory, handleResidenceUpdate, countries.length, threeYearsAgo]);

  const stayInMonths = (residenceYear, residenceMonth, currentYear, currentMonth) => {
    return currentYear * 12 + currentMonth - (residenceYear * 12 + residenceMonth);
  };

  return (
    <PageLayout
      title="Where have you lived in the past 3 years?"
      helpText="Please list it down from the most recent."
    >
      <form onSubmit={handleSubmit}>
        <div>
          <div>
            <ol className="list-hidden">
              <TransitionGroup component={null}>{renderResidenceHistories}</TransitionGroup>
            </ol>
          </div>
          {editState && (
            <div className={!residenceHistory.length ? 'no-top' : ''} data-testid="residence-group">
              <ResidenceSelection
                countries={countries}
                states={states}
                fetchStates={fetchStatesForCountry}
                updateResidence={handleResidenceUpdate}
                setEditState={setEditState}
              />
            </div>
          )}
        </div>
        <div className={editState ? 'mt-8' : 'mt-4'}>
          {residenceHistory.length ? (
            <AddActionBar
              setState={setEditState}
              disable={editState ? false : true}
              child={'+ Add another country'}
            />
          ) : null}
        </div>
        <div className="mt-12">
          {showMinDateMessage && (
            <div className="row-spacer text-error">
              Please provide at least 3 years of residence history
            </div>
          )}
          <Button type="submit" className="niso-button" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

PreviousResidencePage.propTypes = {
  states: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  builderUi: PropTypes.shape({
    residenceCountry: PropTypes.shape({
      value: PropTypes.string.isRequired,
    }),
    residenceState: PropTypes.shape({
      value: PropTypes.string.isRequired,
    }),
    residenceHistory: PropTypes.arrayOf(
      PropTypes.shape({
        countryCode: PropTypes.string.isRequired,
        stateCode: PropTypes.string.isRequired,
        startMonth: PropTypes.number.isRequired,
        startYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }),
    ).isRequired,
  }).isRequired,
  currentResidenceMonth: PropTypes.string.isRequired,
  currentResidenceYear: PropTypes.string.isRequired,
  dispatchFetchStates: PropTypes.func.isRequired,
  dispatchAddResidenceHistory: PropTypes.func.isRequired,
  dispatchRemoveResidenceHistory: PropTypes.func.isRequired,
  dispatchPersistResidenceHistory: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
};

PreviousResidencePage.defaultProps = {
  states: {},
};

export const mapStateToProps = state => ({
  states: subdivisionsSelector(state),
  builderUi: state.builder.ui,
  currentResidenceMonth: state.builder.ui.currentResidenceStartDate.month,
  currentResidenceYear: state.builder.ui.currentResidenceStartDate.year,
});

export const mapDispatchToProps = {
  dispatchFetchStates: fetchStatesRequest,
  dispatchAddResidenceHistory: addResidenceHistory,
  dispatchRemoveResidenceHistory: removeResidenceHistory,
  dispatchPersistResidenceHistory: persistResidenceHistory,
  dispatchWizardNav: navigateWizard,
  dispatchAddPageData: addPageData,
};

export { PreviousResidencePage };

export default connect(mapStateToProps, mapDispatchToProps)(PreviousResidencePage);
